import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Clock, Plus, Check, ChevronDown, Trash2 } from 'lucide-react';
import { getFlatRateItems, FlatRateItem } from '../lib/flat-rates';
import { useBusiness } from '../contexts/BusinessContext';
import { Resource } from '../lib/resources';
import { supabase } from '../lib/supabase';
import FlatRateModal from './FlatRateModal';
import { selectInputContentOnFocus } from '../lib/utils';

interface SelectFlatRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (item: FlatRateItem, hours: number, resourceId: string) => Promise<void>;
  selectedResourceId?: string | null;
  bookingId?: string;
}

export default function SelectFlatRateModal({
  isOpen,
  onClose,
  onAdd,
  selectedResourceId,
  bookingId
}: SelectFlatRateModalProps) {
  const { selectedBusiness, role } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<FlatRateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [selectedItemsData, setSelectedItemsData] = useState<Map<string, {
    item: FlatRateItem;
    hours: number;
    originalHours: number;
  }>>(new Map());
  const [isResourceSelectorOpen, setIsResourceSelectorOpen] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [addedItemIds, setAddedItemIds] = useState<Set<string>>(new Set());

  // Check if current user is a resource
  const checkCurrentResource = async () => {
    if (!supabase) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: resource } = await supabase
        .from('resources')
        .select('*')
        .eq('auth_user_id', session.user.id)
        .single();

      if (resource) {
        setSelectedResource(resource);
      }
    } catch (err) {
      console.error('Error checking current resource:', err);
    }
  };

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      const data = await getFlatRateItems(selectedBusiness.id);
      setItems(data);
      setError(null);
    } catch (err) {
      console.error('Error loading flat rate items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load flat rate items');
    } finally {
      setLoading(false);
    }
  };

  // Load currently added items for this booking
  const loadAddedItems = async () => {
    if (!bookingId || !supabase) return;

    try {
      const { data: addedItems, error } = await supabase
        .from('booking_flat_rates_with_details')
        .select('item_id')
        .eq('booking_id', bookingId);

      if (error) throw error;

      // Get unique item IDs that have been added to this booking
      const itemIds = new Set(addedItems?.map(item => item.item_id) || []);
      setAddedItemIds(itemIds);
    } catch (err) {
      console.error('Error loading added items:', err);
      // Don't show error to user, just log it
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      Promise.all([loadItems(), loadResources(), checkCurrentResource(), loadAddedItems()]);
    }
  }, [isOpen, selectedBusiness?.id, bookingId]);

  const loadResources = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      // Only get active resources
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'active')
        .order('first_name');

      if (error) throw error;
      setResources(data);

      // If selectedResourceId is provided, pre-select that resource
      if (selectedResourceId) {
        const resource = data.find(r => r.id === selectedResourceId);
        if (resource) {
          setSelectedResource(resource);
        }
      }
    } catch (err) {
      console.error('Error loading resources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load resources');
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedItems(new Set());
      setSelectedResource(null);
      setSelectedItemsData(new Map());

    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: FlatRateItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filteredCategories = itemsByCategory.filter(category => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.items.some(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower)
      )
    );
  });

  const handleAddItem = (item: FlatRateItem) => {
    if (selectedItems.has(item.id)) {
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setSelectedItemsData(prev => {
        const next = new Map(prev);
        next.delete(item.id);
        return next;
      });
    } else {
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
      setSelectedItemsData(prev => {
        const next = new Map(prev);
        next.set(item.id, {
          item,
          hours: parseFloat(item.hours.toString()),
          originalHours: item.hours
        });
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    try {
      if (!selectedResource && role === 'admin') {
        throw new Error('Please select a resource to assign these items to');
      }

      // Process each item
      for (const {item, hours, originalHours} of selectedItemsData.values()) {
        // Validate hours
        const finalHours = parseFloat(hours.toString()) || originalHours;
        if (isNaN(finalHours) || finalHours <= 0) {
          throw new Error(`Invalid hours value for ${item.name}`);
        }
        
        await onAdd(item, finalHours, selectedResource?.id || 'unassigned');
      }

      // Clear selections and reload added items to update badges
      setSelectedItems(new Set());
      setSelectedItemsData(new Map());
      await loadAddedItems();
      
      // Only close modal after all items are added successfully
      onClose();
    } catch (err) {
      console.error('Error adding flat rate items:', err);
      setError(err instanceof Error ? err.message : 'Failed to add flat rate items');
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Select Flat Rate Item
            </h2>
          </div>

          {/* Search Bar */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                  style={{ fontSize: '16px' }}
                />
              </div>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-3 font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Add Item
              </button>
            </div>
          </div>

          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {/* Only show resource selector for admin users */}
              {role === 'admin' && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Assign Resource *
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => setIsResourceSelectorOpen(!isResourceSelectorOpen)}
                      className={`w-full text-left px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 ${
                        !selectedResource ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                      style={{ fontSize: '16px' }}
                    >
                      {selectedResource ? (
                        <span>{selectedResource.first_name} {selectedResource.last_name}</span>
                      ) : (
                        <span className="text-red-600">Select a resource (required)</span>
                      )}
                    </button>
                    
                    {isResourceSelectorOpen && (
                      <div className="absolute left-0 mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-10 max-h-60 overflow-y-auto">
                        {resources.map((resource) => (
                          <button
                            key={resource.id}
                            onClick={() => {
                              setSelectedResource(resource);
                              setIsResourceSelectorOpen(false);
                            }}
                            className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors"
                            style={{ fontSize: '16px' }}
                          >
                            {resource.first_name} {resource.last_name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading items...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div className="text-center">
                    <p className="text-sm text-red-600 mb-3">{error}</p>
                    <button
                      onClick={loadItems}
                      className="px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 active:scale-[0.98] rounded-xl transition-all duration-150 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Try again
                    </button>
                  </div>
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-8 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-center">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">No items found</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {searchQuery ? 'Try adjusting your search terms' : 'Add flat rate items to get started'}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => setIsCreateModalOpen(true)}
                      className="inline-flex items-center px-4 py-3 font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 active:scale-[0.98] rounded-xl transition-all duration-150 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add Flat Rate Item
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCategories.map((category) => (
                    <div key={category.id} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <div 
                        className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-pointer active:bg-gray-100 transition-colors"
                        onClick={() => toggleCategory(category.id)}
                        style={{ touchAction: 'manipulation' }}
                      >
                        <div className="flex items-center space-x-2">
                          <ChevronDown 
                            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                              expandedCategories.has(category.id) ? 'rotate-180' : ''
                            }`}
                          />
                          <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>
                          <span className="text-xs text-gray-500">({category.items.length})</span>
                        </div>
                      </div>
                      {expandedCategories.has(category.id) && (
                        <div className="divide-y divide-gray-200">
                          {category.items.map((item) => (
                            <div key={item.id} className="p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-semibold text-gray-900">{item.name}</h4>
                                    {addedItemIds.has(item.id) && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        <Check className="h-3 w-3 mr-1" />
                                        Added
                                      </span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                                  )}
                                  <div className="mt-2 flex items-center text-xs text-gray-500">
                                    <Clock className="h-3.5 w-3.5 mr-1" />
                                    {item.hours} hours
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleAddItem(item)}
                                  disabled={selectedItems.has(item.id)}
                                  className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ml-3 ${
                                    selectedItems.has(item.id) 
                                      ? 'bg-green-100 text-green-800 cursor-not-allowed'
                                      : 'text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                                  }`}
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {selectedItems.has(item.id) ? (
                                    <>
                                      <Check className="h-3.5 w-3.5 mr-1.5" />
                                      Added
                                    </>
                                  ) : (
                                    <>
                                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                                      Add
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {selectedItems.size > 0 && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-gray-900">
                      Selected Items ({selectedItemsData.size})
                    </span>
                    <button
                      onClick={handleSubmit}
                      disabled={selectedItemsData.size === 0}
                      className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Add Selected Items
                    </button>
                  </div>
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                      {error}
                    </div>
                  )}
                  <div className="space-y-3">
                    {Array.from(selectedItemsData.values()).map(({item, hours, originalHours}) => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex-1">
                          <span className="text-sm font-semibold text-gray-900">{item.name}</span>
                          <div className="mt-2 flex items-center space-x-2">
                            <input
                              type="number"
                              min="0.1"
                              step="0.1"
                              value={hours}
                              onChange={(e) => {
                                const newHours = parseFloat(e.target.value);
                                if (newHours > 0) {
                                  setSelectedItemsData(prev => {
                                    const next = new Map(prev);
                                    next.set(item.id, {
                                      item,
                                      hours: newHours,
                                      originalHours: item.hours
                                    });
                                    return next;
                                  });
                                }
                              }}
                              onFocus={selectInputContentOnFocus}
                              className="w-24 h-9 px-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            />
                            <span className="text-sm text-gray-500">hours</span>
                            {hours !== originalHours && (
                              <span className="text-xs text-gray-500">
                                (Default: {originalHours}h)
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddItem(item);
                          }}
                          className="ml-3 p-2 text-red-400 hover:text-red-600 rounded-xl hover:bg-red-50 active:bg-red-100 active:scale-95 transition-all duration-150 touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <FlatRateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          loadItems();
        }}
        businessId={selectedBusiness?.id}
        mode="create"
      />
    </div>
  );

  // Use React Portal to render modal outside parent DOM tree
  // This prevents nesting issues when opened from within other modals
  return createPortal(modalContent, document.body);
}